import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import { getAssetUrl } from '../../utils/helpers';
import { apiPost } from '../../services/api';
import { getPublicCourses } from '../../services/adminService';
import { countries } from '../../utils/countries';
import heroImage from '../../assets/images/hydrabad/hero-hyderabad.webp';
import connectionPhilosophyImage from '../../assets/images/hydrabad/classroom-pranayama-practice.webp';
import centreHeadImage from '../../assets/images/hydrabad/centre-head-hyderabad.webp';
import courseImage200 from '../../assets/images/hydrabad/course-200hr-yttc.webp';
import courseImage300 from '../../assets/images/hydrabad/course-300hr-yttc.webp';
import courseImage500 from '../../assets/images/hydrabad/course-500hr-yttc.webp';
import courseImageKids from '../../assets/images/hydrabad/course-kids-yoga-ttc.webp';
import courseImageDiet from '../../assets/images/hydrabad/course-diet-and-nutrition.webp';
import courseImageFace from '../../assets/images/hydrabad/course-face-yoga-ttc.webp';
import visitCentreImage from '../../assets/images/hydrabad/classroom-asana-practice.webp';
import whyCertifiedIcon from '../../assets/images/hydrabad/certified.webp';
import whyRootedTraditionIcon from '../../assets/images/hydrabad/rooted-tradition.webp';
import whySmallBatchesIcon from '../../assets/images/hydrabad/small-batches.webp';
import whyInstantConfirmationIcon from '../../assets/images/hydrabad/instant-confirmation.webp';
import whySuryaChakara from '../../assets/images/hydrabad/surya-chakara.webp';
import whyGirlCharacter from '../../assets/images/hydrabad/pranaflow-girl-character.webp';
import whyTraditionalLineageIcon from '../../assets/images/hydrabad/traditional-lineage.webp';
import whyWorldWideSupportIcon from '../../assets/images/hydrabad/world-wide-support.webp';
import whyBalanceEnergyIcon from '../../assets/images/hydrabad/balance-energy.webp';
import './HydrabadCenter.css';

// Reusable FAQ Card Component
const HydFaqCard = ({ question, answer, isActive, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState('0px');

  useEffect(() => {
    setHeight(isActive ? `${contentRef.current.scrollHeight}px` : '0px');
  }, [isActive]);

  return (
    <div className={`pf-faq-card ${isActive ? 'active' : ''}`}>
      <div
        className="pf-faq-header"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isActive}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <h3 className="pf-faq-question">{question}</h3>
        <span className="pf-faq-icon" aria-hidden="true">
          <svg className="pf-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </span>
      </div>
      <div
        ref={contentRef}
        className="pf-faq-content"
        style={{ maxHeight: height }}
      >
        <div className="pf-faq-content-inner">
          {answer}
        </div>
      </div>
    </div>
  );
};

const COURSE_OPTIONS = [
  { id: 1, title: '200 Hour Yoga Teacher Training', duration: '200 Hours', img: courseImage200, btnText: 'Explore 200 Hour TTC in Hyderabad', desc: 'Develop your foundation in yoga practice, essential theory, and teaching skills through structured learning and guided practice.' },
  { id: 2, title: '300 Hour Yoga Teacher Training', duration: '300 Hours', img: courseImage300, btnText: 'Explore 300 Hour TTC in Hyderabad', desc: 'Continue your development as a yoga teacher with deeper study, guided practice, and opportunities to strengthen your teaching approach. Ask our team about entry requirements.' },
  { id: 3, title: '500 Hour Yoga Teacher Training', duration: '500 Hours', img: courseImage500, btnText: 'Explore 500 Hour TTC in Hyderabad', desc: 'Explore an extended teacher training pathway that brings foundational and advanced learning together. Our team will explain the course structure and suitable entry route.' },
  { id: 5, title: 'Face Yoga Teacher Training', duration: 'Specialist Course', img: courseImageFace, btnText: 'Explore Face Yoga in Hyderabad', desc: 'Study facial anatomy, face yoga exercises, facial massage, breathwork, and teaching methodology with in-person guidance and practical learning.', link: '/Centre/hydrabad/face-yoga-teacher-training' },
  { id: 6, title: 'Kids Yoga Teacher Training', duration: '95 Hours', img: courseImageKids, btnText: 'Explore Kids Yoga TTC in Hyderabad', desc: 'Learn to plan engaging, age-appropriate yoga sessions for children through movement, breathing, play, and thoughtful teaching.' },
  { id: 4, title: 'Diet & Nutrition Course', duration: 'Specialist Course', img: courseImageDiet, btnText: 'Explore Diet & Nutrition in Hyderabad', desc: 'Build your understanding of nutrition fundamentals, food choices, and everyday eating habits through structured education.' }
];

const FAQS = [
  {
    q: 'Is this centre part of PranaFlow Academy in Rishikesh?',
    a: 'Yes. Our Hyderabad centre is part of PranaFlow Academy, whose main branch is in Rishikesh, Uttarakhand. The centre operates under the academy’s shared teaching and management standards.'
  },
  {
    q: 'Are the Hyderabad courses taught in person?',
    a: 'This page focuses on classroom learning at our Hyderabad centre. Confirm the delivery format and schedule of your chosen batch with our team before enrolling.'
  },
  {
    q: 'Can beginners join?',
    a: 'Some courses welcome beginners, while others have prior-training requirements. Tell us about your experience so we can guide you towards a suitable course.'
  },
  {
    q: 'Who will teach my batch?',
    a: 'Teacher assignments depend on the course and batch. Our team can share the relevant teacher information before you enroll.'
  },
  {
    q: 'Can I join online instead?',
    a: 'Yes. You can explore our live online courses and choose an available online batch that suits your needs.'
  },
  {
    q: 'Are fees and timings the same for online and classroom courses?',
    a: 'They may differ. Our team will explain the fees, schedule, inclusions, and assessment requirements for your selected learning mode.'
  },
  {
    q: 'What certificate will I receive?',
    a: 'Certificate details and completion requirements depend on your chosen programme. Please check the course page or ask our team for the exact certificate information before enrolling.'
  }
];

const HydrabadCenter = () => {
  // SEO Metadata and Structured Data setup
  useEffect(() => {
    document.title = "Yoga Classes & Teacher Training in Hyderabad | PranaFlow";

    // Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', "Explore classroom yoga classes and teacher training at PranaFlow Academy in Hyderabad. Rooted in Rishikesh, with live online courses also available.");

    // Canonical Link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', "https://pranaflowacademy.com/Centre/hydrabad");

    // Open Graph Metadata
    const ogTags = [
      { property: 'og:title', content: "Yoga Classes & Teacher Training in Hyderabad | PranaFlow" },
      { property: 'og:description', content: "Explore classroom yoga classes and teacher training at PranaFlow Academy in Hyderabad. Rooted in Rishikesh, with live online courses also available." },
      { property: 'og:type', content: "website" },
      { property: 'og:url', content: "https://pranaflowacademy.com/Centre/hydrabad" },
      { property: 'og:image', content: "https://pranaflowacademy.com/assets/hydrabad/hero-hyderabad.webp" },
      { property: 'og:site_name', content: "PranaFlow Academy" }
    ];

    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter Metadata
    const twitterTags = [
      { name: 'twitter:card', content: "summary_large_image" },
      { name: 'twitter:title', content: "Yoga Classes & Teacher Training in Hyderabad | PranaFlow" },
      { name: 'twitter:description', content: "Explore classroom yoga classes and teacher training at PranaFlow Academy in Hyderabad. Rooted in Rishikesh, with live online courses also available." },
      { name: 'twitter:image', content: "https://pranaflowacademy.com/assets/hydrabad/hero-hyderabad.webp" }
    ];

    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    window.scrollTo(0, 0);

    // Dynamic JSON-LD Schema
    const scriptId = 'jsonld-schema-hydrabad';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    const orgSchema = {
      "@context": "https://schema.org",
      "@type": ["EducationalOrganization", "Organization"],
      "@id": "https://pranaflowacademy.com/#organization",
      "name": "PranaFlow Academy",
      "legalName": "PranaFlow Academy",
      "url": "https://pranaflowacademy.com/",
      "logo": "https://pranaflowacademy.com/assets/rishikesh_hero.webp",
      "description": "PranaFlow Academy provides classroom yoga teacher training and wellness courses in Hyderabad with its main branch in Rishikesh, Uttarakhand.",
      "email": "pranaflowacademy@gmail.com",
      "telephone": "+919548354084"
    };

    const webpageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://pranaflowacademy.com/Centre/hydrabad#webpage",
      "url": "https://pranaflowacademy.com/Centre/hydrabad",
      "name": "Yoga Classes & Teacher Training in Hyderabad | PranaFlow",
      "description": "Explore classroom yoga classes and teacher training at PranaFlow Academy in Hyderabad. Rooted in Rishikesh, with live online courses also available.",
      "isPartOf": {
        "@type": "WebSite",
        "@id": "https://pranaflowacademy.com/#website",
        "url": "https://pranaflowacademy.com/",
        "name": "PranaFlow Academy"
      },
      "about": {
        "@id": "https://pranaflowacademy.com/#organization"
      }
    };

    script.textContent = JSON.stringify([orgSchema, webpageSchema]);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, []);

  // Courses Catalogue State
  const [coursesList, setCoursesList] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState('');

  // Form State — Face Yoga Parity + Select Course
  const [formData, setFormData] = useState({
    name: '',
    countryCode: '+91',
    phone: '',
    email: '',
    courseId: '',
    learningFormat: 'Classroom Teacher Training',
    preferredTiming: '',
    preferredLanguage: 'English',
    question: ''
  });

  const [activeFaq, setActiveFaq] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch Public Courses Catalogue
  const fetchCourses = async () => {
    setCoursesLoading(true);
    setCoursesError('');
    try {
      const data = await getPublicCourses();
      const list = Array.isArray(data) ? data : (data?.courses || []);
      setCoursesList(list);
    } catch (err) {
      console.error('Failed to load courses catalogue:', err);
      setCoursesError('Unable to load course options. Please retry.');
    } finally {
      setCoursesLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // Smooth scroll handler
  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const headerOffset = 90;
    const elementPosition = el.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  };

  // Pre-fill course and scroll to enquiry
  const handleSelectCourse = (course) => {
    if (coursesList.length > 0) {
      const searchKey = course.title.toLowerCase().replace(' course', '').trim();
      const matched = coursesList.find(c => 
        c.title.toLowerCase().includes(searchKey) || searchKey.includes(c.title.toLowerCase())
      );
      if (matched) {
        setFormData(prev => ({ ...prev, courseId: String(matched.id) }));
      }
    }
    scrollToSection('enquiry-section');
  };

  // Pre-fill visit request and scroll to enquiry
  const handleArrangeVisit = () => {
    setFormData(prev => {
      const visitNote = 'I would like to arrange a visit to the Hyderabad centre to learn more about the classroom arrangements and courses.';
      const currentQ = prev.question.trim();
      return {
        ...prev,
        learningFormat: 'Classroom Teacher Training',
        question: currentQ && !currentQ.includes(visitNote) ? `${visitNote}\n${currentQ}` : (currentQ || visitNote)
      };
    });
    scrollToSection('enquiry-section');
  };

  // Form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (errorMessage) setErrorMessage('');
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!formData.name.trim()) {
      setErrorMessage('Please enter your full name.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email.trim())) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    if (!formData.phone.trim()) {
      setErrorMessage('Please enter your mobile / WhatsApp number.');
      return;
    }

    if (!formData.courseId) {
      setErrorMessage('Please select a course.');
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedCourse = coursesList.find(c => String(c.id) === String(formData.courseId));
      const payload = {
        centre_code: 'HYD-001',
        course_id: parseInt(formData.courseId, 10),
        course_name: selectedCourse ? selectedCourse.title : '',
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobile: formData.phone.trim(),
        mobile_number: formData.phone.trim(),
        country_code: formData.countryCode,
        learning_mode: formData.learningFormat,
        preferred_timing: formData.preferredTiming ? formData.preferredTiming.trim() : '',
        preferred_language: formData.preferredLanguage ? formData.preferredLanguage.trim() : 'English',
        question: formData.question ? formData.question.trim() : '',
        source_page: '/Centre/hydrabad'
      };

      await apiPost('/save-centre-course-enquiry.php', payload);
      setIsSuccess(true);

      // Push to dataLayer for analytics tracking if present
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'pranaflow_centre_enquiry_success',
          centre_code: 'HYD-001',
          lead_source: 'Hyderabad Centre Page',
          learning_mode: formData.learningFormat,
          course: selectedCourse ? selectedCourse.title : ''
        });
      }
    } catch (err) {
      console.error('Hyderabad Centre Enquiry submission error:', err);
      setErrorMessage(err.message || 'Failed to submit enquiry. Please try again or reach out directly on WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="public-site pf-hyd-page">
      <PublicHeader />

      {/* =========================================================================
           SECTION 1 — HERO
           ========================================================================= */}
      <section id="hero" className="pf-hero-section">
        <div className="pf-hero-container">
          <div className="pf-hero-grid">
            {/* Left Content */}
            <div className="pf-hero-content">
              {/* Eyebrow */}
              <div className="pf-hero-eyebrow">
                <span className="pf-hero-eyebrow-dot" aria-hidden="true"></span>
                <span>PranaFlow Academy · Hyderabad Centre</span>
              </div>

              {/* Main Heading H1 */}
              <h1 className="pf-hero-title">
                Yoga Classes & Teacher Training in Hyderabad
              </h1>

              {/* Supporting Line */}
              <p className="pf-hero-support-line">
                Rooted in Rishikesh. Learn with us in Hyderabad.
              </p>

              {/* Paragraph */}
              <p className="pf-hero-desc">
                Experience personal guidance, structured practice, and face-to-face learning at PranaFlow Academy’s Hyderabad centre. With our main branch in Rishikesh, we bring the academy’s commitment to quality education and carefully selected teachers to students in Hyderabad.
              </p>
              <p className="pf-hero-desc" style={{ marginTop: '-12px' }}>
                Whether you want to deepen your practice, begin your teaching journey, or learn a specialist skill, explore a course that fits your goals.
              </p>

              {/* Trust Points (4 Cards) */}
              <div className="pf-hero-trust-grid">
                <div className="pf-hero-trust-card">
                  <span className="pf-hero-trust-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </span>
                  <span className="pf-hero-trust-text">In-person classroom learning</span>
                </div>

                <div className="pf-hero-trust-card">
                  <span className="pf-hero-trust-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 3c-1.5 3-4 5.5-7 6.5 2.5 2 5 2 7-1 2 3 4.5 3 7 1-3-1-5.5-3.5-7-6.5zm-1 8c-2 2-5 3-8 3 2 2 4.5 2.5 7 1.5v5.5h2v-5.5c2.5 1 5 .5 7-1.5-3 0-6-1-8-3z"/>
                    </svg>
                  </span>
                  <span className="pf-hero-trust-text">Carefully selected, qualified teachers</span>
                </div>

                <div className="pf-hero-trust-card">
                  <span className="pf-hero-trust-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </span>
                  <span className="pf-hero-trust-text">Guided practice and opportunities to ask questions</span>
                </div>

                <div className="pf-hero-trust-card">
                  <span className="pf-hero-trust-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2L2 22h20L12 2z" />
                      <path d="M12 6l-8 13h16l-8-13z" />
                    </svg>
                  </span>
                  <span className="pf-hero-trust-text">Part of PranaFlow Academy, with our main branch in Rishikesh</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pf-hero-cta-group">
                <button
                  type="button"
                  className="pf-hero-btn-primary"
                  onClick={() => scrollToSection('hyderabad-courses')}
                >
                  <span>Explore Hyderabad Courses</span>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <polyline points="19 12 12 19 5 12"/>
                  </svg>
                </button>

                <button
                  type="button"
                  className="pf-hero-btn-secondary"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, learningMode: 'Classroom in Hyderabad' }));
                    scrollToSection('enquiry-section');
                  }}
                >
                  <span>Enquire About Classroom Training</span>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Small supporting text */}
              <div className="pf-hero-reassurance" style={{ marginTop: '8px' }}>
                <span className="pf-hero-reassurance-icon" aria-hidden="true">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                </span>
                <span>
                  Prefer learning from home? <Link to="/courses" style={{ color: '#0D443E', fontWeight: 600, textDecoration: 'underline' }}>Explore our live online courses</Link>.
                </span>
              </div>
            </div>

            {/* Right Media */}
            <div className="pf-hero-media">
              <div className="pf-hero-frame">
                <div className="pf-hero-img-wrap">
                  {/* Location badge */}
                  <div className="pf-hero-loc-pill">
                    <span className="pf-hero-loc-icon" aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                    </span>
                    <span>Hyderabad Centre</span>
                  </div>

                  <img
                    src={heroImage}
                    alt="PranaFlow Academy yoga instruction rooted in Rishikesh tradition"
                    className="pf-hero-img"
                    width="1254"
                    height="1254"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
           SECTION 2 — OUR RISHIKESH CONNECTION
           ========================================================================= */}
      <section className="pf-hyd-section pf-hyd-section-tint">
        <div className="pf-hyd-container">
          <div className="pf-hyd-connection-grid">
            <div className="pf-hyd-connection-media">
              <div className="pf-hyd-img-frame">
                <img
                  src={connectionPhilosophyImage}
                  alt="Students practicing pranayama breathwork in a yoga classroom session"
                  width="1024"
                  height="679"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="pf-hyd-connection-content">
              <div className="pf-hyd-eyebrow">
                <span className="pf-hyd-eyebrow-dot" aria-hidden="true"></span>
                <span>One Academy. Shared Teaching Standards.</span>
              </div>

              <h2 className="pf-hyd-h2">
                Our Main Branch Is in Rishikesh. Our Hyderabad Centre Brings Learning Closer to You.
              </h2>

              <p>
                PranaFlow Academy’s main branch is in Rishikesh, Uttarakhand. Our Hyderabad centre is part of the same academy, created for students who want to learn with us through in-person classes closer to home.
              </p>
              <p>
                Our approach remains the same across the academy: choose teachers carefully, explain clearly, guide students patiently, and put the quality of learning first.
              </p>
              <p>
                At Hyderabad, you can meet your teachers, practise alongside fellow students, and receive guidance in a classroom setting, with a dedicated centre team supporting your learning.
              </p>

              <div>
                <Link to="/about-us" className="pf-hyd-btn-secondary-link">
                  <span>Discover PranaFlow Academy</span>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
           SECTION 3 — COURSES AT HYDERABAD
           ========================================================================= */}
      <section id="hyderabad-courses" className="pf-hyd-section pf-hyd-section-white">
        <div className="pf-hyd-container">
          <div className="pf-hyd-courses-header">
            <div className="pf-hyd-eyebrow">
              <span className="pf-hyd-eyebrow-dot" aria-hidden="true"></span>
              <span>Explore Your Learning Path</span>
            </div>
            <h2 className="pf-hyd-h2">
              Yoga Teacher Training & Specialist Courses in Hyderabad
            </h2>
            <p className="pf-hyd-lead-p">
              Build a foundation, continue your teacher training, or explore a specialist subject. Speak with our team about classroom batches, eligibility, fees, and the right starting point for you.
            </p>
          </div>

          <div className="pf-hyd-courses-grid">
            {COURSE_OPTIONS.map((course) => (
              <div className="pf-hyd-course-card" key={course.id}>
                <div className="pf-hyd-card-img-wrap">
                  <img src={course.img} alt={course.title} loading="lazy" />
                  <span className="pf-hyd-badge-duration">{course.duration}</span>
                </div>

                <div className="pf-hyd-card-body">
                  <h3 className="pf-hyd-card-title">{course.title}</h3>
                  <p className="pf-hyd-card-desc">{course.desc}</p>

                  <div className="pf-hyd-card-info-rows">
                    <div className="pf-hyd-card-info-row">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span>Classroom in Hyderabad</span>
                    </div>
                    <div className="pf-hyd-card-info-row">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span>{course.duration} Guided Training</span>
                    </div>
                  </div>

                  {course.link ? (
                    <Link
                      to={course.link}
                      className="pf-hyd-card-btn"
                    >
                      <span>{course.btnText}</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                        <path d="M7 17L17 7M17 7H7M17 7V17" />
                      </svg>
                    </Link>
                  ) : (
                    <button
                      type="button"
                      className="pf-hyd-card-btn"
                      onClick={() => handleSelectCourse(course)}
                    >
                      <span>{course.btnText}</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                        <path d="M7 17L17 7M17 7H7M17 7V17" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="pf-hyd-courses-footer">
            <p className="pf-hyd-courses-footer-note">
              Classroom schedules and course availability vary by batch. Contact us for confirmed dates, teaching arrangements, fees, and certificate details.
            </p>
            <Link to="/courses" className="pf-hyd-courses-footer-link">
              Looking for online learning? View Our Live Online Courses →
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================================================
           SECTION 4 — CLASSROOM LEARNING
           ========================================================================= */}
      <section className="pf-hyd-section pf-hyd-section-tint">
        <div className="pf-hyd-container">
          <div className="text-center" style={{ maxWidth: '800px', margin: '0 auto 36px auto' }}>
            <div className="pf-hyd-eyebrow">
              <span className="pf-hyd-eyebrow-dot" aria-hidden="true"></span>
              <span>Learn Together. Practise with Guidance.</span>
            </div>
            <h2 className="pf-hyd-h2">
              Make Space for Learning Beyond a Screen
            </h2>
            <p className="pf-hyd-lead-p">
              Classroom learning gives you time to practise, ask questions, and discuss what you are learning with your teacher and fellow students. At our Hyderabad centre, we focus on helping you understand the subject and develop your skills through participation.
            </p>
          </div>

          <div className="pf-hyd-benefits-grid">
            <div className="pf-hyd-benefit-card">
              <div className="pf-hyd-benefit-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3 className="pf-hyd-benefit-title">Guidance while you practise</h3>
              <p className="pf-hyd-benefit-desc">
                Receive feedback on your technique and ask for clarification as you learn.
              </p>
            </div>

            <div className="pf-hyd-benefit-card">
              <div className="pf-hyd-benefit-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <h3 className="pf-hyd-benefit-title">Face-to-face interaction</h3>
              <p className="pf-hyd-benefit-desc">
                Discuss concepts directly with your teacher and build understanding through conversation.
              </p>
            </div>

            <div className="pf-hyd-benefit-card">
              <div className="pf-hyd-benefit-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <h3 className="pf-hyd-benefit-title">A structured learning routine</h3>
              <p className="pf-hyd-benefit-desc">
                Follow your batch schedule and make regular time for your practice and studies.
              </p>
            </div>

            <div className="pf-hyd-benefit-card">
              <div className="pf-hyd-benefit-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3 className="pf-hyd-benefit-title">Learn alongside others</h3>
              <p className="pf-hyd-benefit-desc">
                Share the classroom with students working towards their own practice and teaching goals.
              </p>
            </div>
          </div>

          <div className="pf-hyd-center-btn-wrap">
            <button
              type="button"
              className="pf-hyd-btn-secondary-link"
              onClick={() => {
                setFormData(prev => ({ ...prev, learningFormat: 'Classroom Teacher Training' }));
                scrollToSection('enquiry-section');
              }}
            >
              <span>Ask About the Classroom Experience</span>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* =========================================================================
           SECTION 5 — CENTRE HEAD
           ========================================================================= */}
      <section className="pf-hyd-section pf-hyd-section-white">
        <div className="pf-hyd-container">
          <div className="pf-hyd-ch-card">
            {/* Main Profile Grid: Photo Left, Intro & Details Right */}
            <div className="pf-hyd-ch-profile-grid">
              {/* Left: Centre Head Photo */}
              <div className="pf-hyd-ch-photo-col">
                <div className="pf-hyd-ch-photo-frame">
                  <img
                    src={centreHeadImage}
                    alt="Hyderabad Centre Head at PranaFlow Academy."
                    className="pf-hyd-ch-photo"
                    width="303"
                    height="330"
                    loading="lazy"
                  />
                  <div className="pf-hyd-ch-photo-badge">
                    <span className="pf-hyd-ch-photo-badge-dot" aria-hidden="true"></span>
                    <span>Hyderabad Centre Head</span>
                  </div>
                </div>
              </div>

              {/* Right: Intro & Details */}
              <div className="pf-hyd-ch-info-col">
                <div className="pf-hyd-eyebrow">
                  <span className="pf-hyd-eyebrow-dot" aria-hidden="true"></span>
                  <span>Meet Your Centre Head</span>
                </div>
                <h2 className="pf-hyd-h2" style={{ marginBottom: '8px' }}>
                  A Dedicated Lead for Your Learning Experience in Hyderabad
                </h2>
                <div className="pf-hyd-ch-role">
                  <strong>Centre Head — PranaFlow Academy, Hyderabad</strong>
                </div>
                <div className="pf-hyd-ch-name">
                  <strong>Dasari Nanditha</strong>
                </div>
                <div className="pf-hyd-ch-qualifications">
                  <strong>Dietitian &amp; Yoga Teacher | 7 Years of Experience</strong>
                </div>
                <p className="pf-hyd-ch-intro">
                  Dasari Nanditha is a <strong>Dietitian &amp; Yoga Teacher with 7 years of experience</strong>, bringing together knowledge of nutrition, yoga, and practical wellness education. She has taught <strong>800+ students</strong>, including <strong>300+ in Face Yoga, 300+ in Diet and Nutrition, and 250+ through PCOS &amp; PCOD-focused wellness guidance</strong>.
                </p>
                <p className="pf-hyd-ch-intro">
                  She brings this experience to PranaFlow Academy’s Hyderabad centre, supporting students through clear explanations, guided practice, and a patient teaching approach. Working with the academy’s central team, she also coordinates teachers, batch schedules, and student support to maintain an organised and supportive learning environment.
                </p>
                <div>
                  <button
                    type="button"
                    className="pf-hyd-btn-secondary-link"
                    onClick={() => scrollToSection('enquiry-section')}
                    style={{ color: '#FFFFFF' }}
                  >
                    <span style={{ color: '#FFFFFF' }}>
                      <strong style={{ color: '#FFFFFF' }}>Speak with Our Hyderabad Team</strong>
                    </span>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom Support Section: Responsibilities Grid */}
            <div className="pf-hyd-ch-resp-box">
              <h3 className="pf-hyd-ch-resp-title">How Your Centre Head Supports You</h3>
              <div className="pf-hyd-ch-resp-grid">
                <div className="pf-hyd-ch-resp-item">
                  <span className="pf-hyd-ch-list-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span>Coordinates classroom schedules and teacher availability.</span>
                </div>
                <div className="pf-hyd-ch-resp-item">
                  <span className="pf-hyd-ch-list-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span>Helps students understand their batch arrangements.</span>
                </div>
                <div className="pf-hyd-ch-resp-item">
                  <span className="pf-hyd-ch-list-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span>Collects feedback about classes and the learning experience.</span>
                </div>
                <div className="pf-hyd-ch-resp-item">
                  <span className="pf-hyd-ch-list-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span>Follows up on student questions and concerns.</span>
                </div>
                <div className="pf-hyd-ch-resp-item">
                  <span className="pf-hyd-ch-list-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span>Works with the academy’s central team to maintain teaching standards.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* =========================================================================
           SECTION 6 — WHY STUDENTS CHOOSE PRANAFLOW ACADEMY
           ========================================================================= */}
      <section className="section-padding pf-why-section" id="why-choose" style={{ backgroundColor: '#ffffff' }}>
        <div className="container">
          <div className="text-center" style={{ marginBottom: '40px' }}>
            <span className="section-meta">Why Choose Our Hyderabad Centre</span>
            <h2 className="section-title">Quality Yoga Education in Hyderabad, with Guidance at Every Step</h2>
            <p className="section-desc">Learn with qualified teachers, choose a learning mode that suits you, and understand your course before you enrol. At PranaFlow Academy’s Hyderabad Centre, quality teaching and student support come first.</p>
          </div>

          <div className="pf-why-grid">
            <ul className="pf-why-col pf-why-col--left">
              <li className="pf-why-point">
                <div className="pf-why-point-text">
                  <h3>Carefully Selected Teachers</h3>
                  <p>We select teachers based on relevant qualifications, subject knowledge, teaching experience, and their ability to guide students with patience and clarity.</p>
                </div>
                <span className="pf-why-point-icon" aria-hidden="true">
                  <img src={whyCertifiedIcon} alt="Carefully Selected Teachers icon" width="70" height="70" />
                </span>
              </li>
              <li className="pf-why-point">
                <div className="pf-why-point-text">
                  <h3>Learning That Fits Your Needs</h3>
                  <p>Explore classroom training, one-to-one teacher training, personal practice classes, and live online learning. Contact us to confirm the options available for your chosen course.</p>
                </div>
                <span className="pf-why-point-icon" aria-hidden="true">
                  <img src={whyRootedTraditionIcon} alt="Learning That Fits Your Needs icon" width="70" height="70" />
                </span>
              </li>
              <li className="pf-why-point">
                <div className="pf-why-point-text">
                  <h3>Rishikesh Roots, Hyderabad Access</h3>
                  <p>Learn with a yoga academy rooted in Rishikesh, with opportunities to study in Hyderabad and build your understanding through guided practice.</p>
                </div>
                <span className="pf-why-point-icon" aria-hidden="true">
                  <img src={whySmallBatchesIcon} alt="Rishikesh Roots, Hyderabad Access icon" width="70" height="70" />
                </span>
              </li>
              <li className="pf-why-point">
                <div className="pf-why-point-text">
                  <h3>Structured Classes and Clear Guidance</h3>
                  <p>Follow an organised course schedule with clear explanations, practical learning, and opportunities to ask questions as you progress.</p>
                </div>
                <span className="pf-why-point-icon" aria-hidden="true">
                  <img src={whyInstantConfirmationIcon} alt="Structured Classes and Clear Guidance icon" width="70" height="70" />
                </span>
              </li>
            </ul>

            <div className="pf-why-media">
              <img className="pf-why-flower" src={whySuryaChakara} alt="Decorative mandala pattern" width="507" height="347" aria-hidden="true" />
              <img className="pf-why-figure" src={whyGirlCharacter} alt="Student meditating in lotus pose" width="1208" height="1302" />
            </div>

            <ul className="pf-why-col pf-why-col--right">
              <li className="pf-why-point">
                <span className="pf-why-point-icon" aria-hidden="true">
                  <img src={whyTraditionalLineageIcon} alt="Student Dashboard and Learning Support icon" width="70" height="70" />
                </span>
                <div className="pf-why-point-text">
                  <h3>Student Dashboard and Learning Support</h3>
                  <p>After enrolment, access your student dashboard for course information, batch updates, and the learning resources available with your programme.</p>
                </div>
              </li>
              <li className="pf-why-point">
                <span className="pf-why-point-icon" aria-hidden="true">
                  <img src={whyWorldWideSupportIcon} alt="Clear Fees and Certificate Details icon" width="70" height="70" />
                </span>
                <div className="pf-why-point-text">
                  <h3>Clear Fees and Certificate Details</h3>
                  <p>Understand your course fees, payment options, completion requirements, and the certificate you will receive before making an enrolment decision.</p>
                </div>
              </li>
              <li className="pf-why-point">
                <span className="pf-why-point-icon" aria-hidden="true">
                  <img src={whyBalanceEnergyIcon} alt="A Team You Can Speak To icon" width="70" height="70" />
                </span>
                <div className="pf-why-point-text">
                  <h3>A Team You Can Speak To</h3>
                  <p>Ask us about your course, teacher’s credentials, preferred language, and class timings. We help you understand your options so you can choose with confidence.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>


      {/* =========================================================================
           SECTION 7 — ONLINE OPTION
           ========================================================================= */}
      <section className="pf-hyd-section pf-hyd-section-white">
        <div className="pf-hyd-container">
          <div className="pf-hyd-online-card">
            <div className="pf-hyd-eyebrow">
              <span className="pf-hyd-eyebrow-dot" aria-hidden="true"></span>
              <span>Another Way to Learn with PranaFlow</span>
            </div>
            <h2 className="pf-hyd-h2">
              Unable to Attend in Hyderabad? Join Us Live Online.
            </h2>
            <p className="pf-hyd-lead-p" style={{ maxWidth: '750px', margin: '0 auto 16px auto' }}>
              If travel, distance, or your schedule makes classroom attendance difficult, explore PranaFlow Academy’s live online courses.
            </p>
            <p style={{ maxWidth: '750px', margin: '0 auto 24px auto', fontSize: '0.95rem', color: '#4A5568', lineHeight: 1.6 }}>
              Learn through scheduled online classes with teacher interaction and guidance. Check your chosen course for batch timings, teacher details, recordings, learning resources, and assessment requirements.
            </p>

            <div className="pf-hyd-online-actions">
              <Link to="/courses" className="pf-hero-btn-primary">
                <span>Explore Live Online Courses</span>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
              <button
                type="button"
                className="pf-hero-btn-secondary"
                onClick={() => {
                  setFormData(prev => ({ ...prev, learningFormat: 'Live Online' }));
                  scrollToSection('enquiry-section');
                }}
              >
                <span>Help Me Choose a Learning Mode</span>
              </button>
            </div>

            <p className="pf-hyd-online-support-text">
              Our team can explain the available classroom and online options for your course.
            </p>
          </div>
        </div>
      </section>

      {/* =========================================================================
           SECTION 8 — VISIT THE CENTRE
           ========================================================================= */}
      <section className="pf-hyd-section pf-hyd-section-tint">
        <div className="pf-hyd-container">
          <div className="pf-hyd-visit-box">
            <div className="pf-hyd-visit-grid">
              {/* Text & Intro */}
              <div className="pf-hyd-visit-intro">
                <div className="pf-hyd-eyebrow">
                  <span className="pf-hyd-eyebrow-dot" aria-hidden="true"></span>
                  <span>Find Us in Hyderabad</span>
                </div>
                <h2 className="pf-hyd-h2">
                  Get to Know the Place Where You Will Learn
                </h2>
                <p className="pf-hyd-lead-p" style={{ marginBottom: 0 }}>
                  Speak with our team about the centre, your preferred course, and classroom arrangements. Contact us to arrange a visit and confirm when someone will be available to guide you.
                </p>
              </div>

              {/* Media / Image */}
              <div className="pf-hyd-visit-media">
                <div className="pf-hyd-visit-img-frame">
                  <img
                    src={visitCentreImage}
                    alt="Yoga students practicing downward-facing dog pose in an asana training class"
                    className="pf-hyd-visit-img"
                    width="1024"
                    height="793"
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Centre Details & Actions */}
              <div className="pf-hyd-visit-bottom">
                <div className="pf-hyd-visit-meta">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0D443E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  <span>Centre: PranaFlow Academy — Hyderabad</span>
                </div>

                <div className="pf-hyd-visit-actions">
                  <button
                    type="button"
                    className="pf-hero-btn-primary"
                    onClick={handleArrangeVisit}
                  >
                    <span>Arrange a Centre Visit</span>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                  <a
                    href="https://wa.me/919548354084?text=Hello%20PranaFlow%20Academy%2C%20I%20would%20like%20to%20arrange%20a%20visit%20to%20the%20Hyderabad%20centre."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pf-hero-btn-secondary"
                  >
                    <span>WhatsApp Our Team</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
           SECTION 9 — FREQUENTLY ASKED QUESTIONS
           ========================================================================= */}
      <section className="pf-hyd-section pf-hyd-section-white">
        <div className="pf-hyd-container pf-hyd-faq-container">
          <div className="text-center" style={{ marginBottom: '44px' }}>
            <div className="pf-hyd-eyebrow">
              <span className="pf-hyd-eyebrow-dot" aria-hidden="true"></span>
              <span>Frequently Asked Questions</span>
            </div>
            <h2 className="pf-hyd-h2">
              Questions About Learning at Our Hyderabad Centre
            </h2>
          </div>

          <div className="pf-faq-accordion">
            {FAQS.map((faq, idx) => (
              <HydFaqCard
                key={idx}
                question={faq.q}
                answer={faq.a}
                isActive={activeFaq === idx}
                onToggle={() => setActiveFaq(activeFaq === idx ? null : idx)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* =========================================================================
           SECTION 10 — FINAL ENQUIRY
           ========================================================================= */}
      <section id="enquiry-section" className="pf-hyd-enquiry-section">
        <div className="pf-hyd-container">
          <div className="pf-hyd-enquiry-wrap">
            {/* Left Column */}
            <div className="pf-hyd-enquiry-left">
              <div className="pf-hyd-eyebrow" style={{ backgroundColor: 'rgba(255, 255, 255, 0.12)', color: '#F3F4F6' }}>
                <span className="pf-hyd-eyebrow-dot" style={{ backgroundColor: '#D28B47' }} aria-hidden="true"></span>
                <span>Let’s Find Your Starting Point</span>
              </div>
              <h2>
                Start Your Learning Journey with PranaFlow Academy in Hyderabad
              </h2>
              <p>
                Tell us what you want to learn. Our team will help you understand the available courses, classroom schedules, fees, and enrollment process so you can choose with clarity.
              </p>

              <div className="pf-hyd-contact-cards">
                <a
                  href="https://wa.me/919548354084?text=Hello%20PranaFlow%20Academy%2C%20I%20have%20an%20enquiry%20regarding%20courses%20at%20the%20Hyderabad%20centre."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pf-hyd-contact-item"
                >
                  <div className="pf-hyd-contact-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.008.01c-6.611 0-11.951 5.338-11.951 11.938 0 2.097.546 4.142 1.587 5.946L0 24l6.59-4.846c1.758.954 3.726 1.455 5.731 1.456 6.613 0 11.949-5.34 11.949-11.997-.002-6.604-5.342-12.003-12.262-12.609zm6.331 17.078c-.242.679-1.399 1.298-1.958 1.382-.501.075-1.136.106-1.833-.115-.422-.135-.964-.314-1.658-.613-2.916-1.256-4.82-4.191-4.965-4.385-.145-.194-1.186-1.576-1.186-3.006s.751-2.133 1.017-2.424c.266-.291.581-.364.775-.364h.556c.178.009.417-.069.653.5.242.581.822 2.011.895 2.156.074.145.122.315.025.509-.096.194-.145.315-.29.485-.146.17-.305.379-.436.509-.145.144-.297.301-.127.591.169.291.752 1.244 1.616 2.015 1.109.989 2.045 1.296 2.335 1.441.29.145.46.121.629-.073.169-.194.725-.847.919-1.138.194-.291.387-.242.653-.145.266.096 1.692.799 1.982.944.29.146.484.219.556.339.073.121.073.703-.169 1.382z"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: '#9CA3AF' }}>WhatsApp Enquiry</div>
                    <div style={{ fontWeight: 600 }}>+91 95483 54084</div>
                  </div>
                </a>

                <a
                  href="mailto:pranaflowacademy@gmail.com"
                  className="pf-hyd-contact-item"
                >
                  <div className="pf-hyd-contact-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', color: '#9CA3AF' }}>Email Us Directly</div>
                    <div style={{ fontWeight: 600 }}>pranaflowacademy@gmail.com</div>
                  </div>
                </a>
              </div>

              <div className="pf-hyd-closing-brand">
                PranaFlow Academy — Main Branch: Rishikesh, Uttarakhand · Hyderabad Centre
              </div>
            </div>

            {/* Right Form Card */}
            <div className="fy-enquiry-card" id="enquiry-form">
              <h3 className="fy-enquiry-title">Enquire About Hyderabad Courses</h3>

              {isSuccess ? (
                <div className="fy-form-alert fy-form-alert-success text-center">
                  <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '1.4rem' }}></i>
                  <h4 style={{ fontWeight: 700, fontSize: '1.1rem', margin: '8px 0 6px' }}>Enquiry Received!</h4>
                  <p style={{ margin: 0 }}>
                    Thank you for reaching out. Our Hyderabad team will contact you shortly with batch schedules, fees, and training details.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {errorMessage && (
                    <div className="fy-form-alert fy-form-alert-error">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  {/* 1. Full Name */}
                  <div className="fy-form-group">
                    <label htmlFor="name" className="fy-form-label">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="fy-form-input"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* 2. Mobile / WhatsApp Number */}
                  <div className="fy-form-group">
                    <label htmlFor="phone" className="fy-form-label">Mobile / WhatsApp Number *</label>
                    <div className="fy-phone-row">
                      <select
                        name="countryCode"
                        className="fy-form-select fy-phone-cc"
                        value={formData.countryCode}
                        onChange={handleInputChange}
                        aria-label="Country Code"
                      >
                        {countries.map((c, i) => (
                          <option key={`${c.code}-${i}`} value={c.dial}>
                            {c.code.toUpperCase()} {c.dial}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="fy-form-input"
                        placeholder="WhatsApp mobile number"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  {/* 3. Email Address */}
                  <div className="fy-form-group">
                    <label htmlFor="email" className="fy-form-label">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="fy-form-input"
                      placeholder="name@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  {/* 4. Select Course */}
                  <div className="fy-form-group">
                    <label htmlFor="courseId" className="fy-form-label">Select Course *</label>
                    {coursesLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 14px', background: '#FCFBF9', border: '1px solid #CBE0D2', borderRadius: '12px', fontSize: '0.9rem', color: '#5C6660' }}>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '1rem', height: '1rem', borderWidth: '2px' }}></span>
                        <span>Loading courses...</span>
                      </div>
                    ) : coursesError ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', background: '#FDF2F2', border: '1px solid #F8B4B4', borderRadius: '12px' }}>
                        <div style={{ color: '#9B1C1C', fontSize: '0.88rem' }}>
                          <i className="bi bi-exclamation-circle-fill me-1"></i> {coursesError}
                        </div>
                        <button
                          type="button"
                          className="fy-btn-secondary"
                          style={{ padding: '6px 14px', fontSize: '0.82rem', alignSelf: 'flex-start' }}
                          onClick={fetchCourses}
                        >
                          <i className="bi bi-arrow-clockwise me-1"></i> Retry Loading Courses
                        </button>
                      </div>
                    ) : (
                      <select
                        id="courseId"
                        name="courseId"
                        className="fy-form-select"
                        value={formData.courseId}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select a course</option>
                        {coursesList.map((course) => (
                          <option key={course.id} value={course.id}>
                            {course.title}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* 5. Preferred Learning Mode */}
                  <div className="fy-form-group">
                    <label className="fy-form-label">Preferred Learning Mode *</label>
                    <div className="fy-radio-group">
                      {[
                        'Classroom Teacher Training',
                        'One-to-One Teacher Training',
                        'Personal Practice Classes',
                        'Live Online'
                      ].map((fmt, idx) => (
                        <label
                          key={idx}
                          className={`fy-radio-option ${formData.learningFormat === fmt ? 'selected' : ''}`}
                        >
                          <input
                            type="radio"
                            name="learningFormat"
                            value={fmt}
                            checked={formData.learningFormat === fmt}
                            onChange={handleInputChange}
                          />
                          <span>{fmt}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* 6. Preferred days and timings */}
                  <div className="fy-form-group">
                    <label htmlFor="preferredTiming" className="fy-form-label">Preferred days and timings</label>
                    <input
                      type="text"
                      id="preferredTiming"
                      name="preferredTiming"
                      className="fy-form-input"
                      placeholder="e.g. Weekday mornings, Weekend batch"
                      value={formData.preferredTiming}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* 7. Preferred teaching language */}
                  <div className="fy-form-group">
                    <label htmlFor="preferredLanguage" className="fy-form-label">Preferred teaching language</label>
                    <select
                      id="preferredLanguage"
                      name="preferredLanguage"
                      className="fy-form-select"
                      value={formData.preferredLanguage}
                      onChange={handleInputChange}
                    >
                      <option value="English">English</option>
                      <option value="Telugu (subject to confirmation)">Telugu — తెలుగు (Subject to confirmation)</option>
                      <option value="Hindi">Hindi</option>
                    </select>
                  </div>

                  {/* 8. Your question */}
                  <div className="fy-form-group">
                    <label htmlFor="question" className="fy-form-label">Your question</label>
                    <textarea
                      id="question"
                      name="question"
                      className="fy-form-textarea"
                      placeholder="Ask about batch start dates, one-to-one availability, fees, or course details..."
                      value={formData.question}
                      onChange={handleInputChange}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="fy-btn-primary w-100"
                    disabled={isSubmitting}
                    style={{ padding: '14px', fontSize: '1rem', marginTop: '8px' }}
                  >
                    {isSubmitting ? 'Submitting Details...' : 'Request Course Details'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default HydrabadCenter;
